/**
 * Scripted camera moves.
 *
 * A cinematic is a timed blend between two *poses* expressed in the same
 * parameters the live rig solves for, so a cinematic can start from whatever
 * the follow camera was doing and land exactly back on it. A pose field left
 * `null` means "whatever the live follow rig wants right now", which is what
 * lets the opening sweep dissolve into gameplay without a cut.
 *
 *   yawRel  radians relative to the rig yaw when the move was triggered
 *   pitch   look-down angle, negative = looking down
 *   dist    boom length in metres
 *   height  extra camera lift above the pivot (0 keeps `pitch` the true pitch)
 *   fov     vertical field of view in degrees
 *   focusY  height of the aim point above the player's feet
 *   lead    velocity look-ahead scale (0 disables the run lead)
 */
import { smootherstep, smoothstep, ease } from '../core/mathx.js';

const pose = (yawRel, pitch, dist, height, fov, focusY, lead) =>
  ({ yawRel, pitch, dist, height, fov, focusY, lead });

export const CINEMATICS = {
  /**
   * Mission opening. Starts high and wide with the battlefield across the
   * frame, falls in behind the hero over five seconds and hands back to the
   * follow rig with no cut. Deliberately NOT automatic — `world.camrig
   * .cinematic('intro')` runs it, so every existing capture keeps its framing.
   */
  /*
   * NB the `height` trap. `height` lifts the lens WITHOUT re-aiming it, so the
   * true look-down is `atan((height - sin(pitch)*dist) / (cos(pitch)*dist))`,
   * not `pitch`. This pose used to read `height: 7.0` on top of a -0.395 rad
   * boom, which is a 30.2° look-down rather than the 22.6° the number implies:
   * the horizon landed at NDC 1.44, i.e. off the top of the frame, and the
   * opening establishing shot was a top-down RTS map view with 0.00 % sky and
   * the fort out of shot. The lift is in the boom now (`height: 0`, so `pitch`
   * IS the tilt), which is the same correction the gameplay pose got: 12.6°
   * down from 13.6 m up and 44.9 m back puts the horizon at 22 % of frame
   * height with the valley reading across the bottom two thirds.
   */
  intro: {
    dur: 5.4, orbit: -0.085, ease: 'smootherstep', exit: 'follow', hold: false,
    from: pose(2.30, -0.220, 46, 0, 44, 3.6, 0),
    to: pose(0, null, null, null, null, null, null),
  },

  /** Musou finisher: a short hero-framed hold on the blow that lands. */
  musouHold: {
    dur: 0.80, orbit: 0.20, ease: 'outCubic', exit: 'prev', hold: false, blendIn: 0.10,
    from: null,
    // chest height, nearly level, close: the hero reads against the sky
    to: pose(0.12, -0.020, 5.4, 0.0, 45, 1.75, 0.25),
  },

  /** The hero goes down: drop low, close in, orbit slowly, hold. */
  death: {
    dur: 3.2, orbit: 0.33, ease: 'outQuart', exit: null, hold: true,
    from: null,
    // low and close. `height` stays positive so the boom does not fight the
    // 1 m ground floor, which would otherwise cancel the drop.
    to: pose(0.55, 0.020, 4.7, 0.25, 48, 1.05, 0),
  },

  /** Mission won: rise, pull out to a low hero angle, hold with a slow orbit. */
  victory: {
    dur: 3.4, orbit: 0.24, ease: 'outCubic', exit: null, hold: true,
    from: null,
    // a low hero angle looking slightly UP, so the banner sky fills the frame
    to: pose(0.42, 0.060, 6.2, 0.10, 46, 1.35, 0),
  },

  /** Officer duel intro / base capture beat: a quick 90-degree arc in. */
  duel: {
    dur: 1.6, orbit: 0.42, ease: 'outQuart', exit: 'follow', hold: false,
    // same 11.0° arc-in as before, with the lift moved out of `height` and
    // into the boom so the number and the tilt agree
    from: pose(1.15, -0.192, 11.5, 0, 46, 1.9, 0),
    to: pose(0, null, null, null, null, null, null),
  },
};

const EASES = {
  smootherstep, smoothstep,
  outCubic: ease.outCubic, outQuart: ease.outQuart, outExpo: ease.outExpo,
  inOutSine: ease.inOutSine, linear: ease.linear,
};

export function easeFn(name) { return EASES[name] || smootherstep; }

/** lerp one pose field, treating null in `to` as "use the live value". */
export function poseField(from, to, live, key, p) {
  const a = from && from[key] !== null && from[key] !== undefined ? from[key] : live;
  const b = to && to[key] !== null && to[key] !== undefined ? to[key] : live;
  return a + (b - a) * p;
}
